import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchAuditLogs } from '@/services/audit';
import { queryKeys } from '@/lib/queryKeys';
import { parseDatabaseError } from '@/lib/errors';
import { AuditLogRecord } from '@/types/models';
import { ErrorAlert } from '@/components/common/ErrorAlert';
import { Pagination } from '@/components/common/Pagination';
import { SearchInput } from '@/components/common/SearchInput';
import {
  ChevronDown,
  ChevronUp,
  CalendarDays,
  User,
  Loader2,
  ShieldCheck,
  Activity,
} from 'lucide-react';
import { PAGE_SIZE } from '@/lib/constants';

function formatDate(iso: string): string {
  return new Date(iso).toLocaleString('en-PK', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export const AuditPage: React.FC = () => {
  const [entityType, setEntityType] = useState('');
  const [action, setAction] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [expandedId, setExpandedId] = useState<number | null>(null);

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: queryKeys.audit.list({ entityType, action, search, page }),
    queryFn: () => fetchAuditLogs({ entityType, action, search, page }),
    placeholderData: (prev) => prev,
  });

  const logs = data?.data ?? [];
  const totalCount = data?.count ?? 0;

  const handleSearch = (val: string) => {
    setSearch(val);
    setPage(0);
  };

  return (
    <div className="space-y-5 text-xs">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-lg font-bold text-brand-black flex items-center space-x-2">
            <Activity className="w-5 h-5 text-brand-green" />
            <span>System Audit Log</span>
          </h1>
          <p className="text-brand-muted mt-0.5">
            {isLoading ? 'Loading…' : `${totalCount.toLocaleString('en-PK')} system audit records`}
          </p>
        </div>

        <div className="px-3 py-1.5 bg-amber-50 border border-amber-200 rounded text-amber-900 font-semibold flex items-center space-x-1.5">
          <ShieldCheck className="w-4 h-4 text-amber-600 shrink-0" />
          <span>Owner Secret Security Audit Access</span>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-2">
        <div className="flex-1">
          <SearchInput value={search} onChange={handleSearch} placeholder="Search entity or action..." />
        </div>

        <div className="relative">
          <select
            value={entityType}
            onChange={(e) => {
              setEntityType(e.target.value);
              setPage(0);
            }}
            className="h-9 pl-3 pr-8 bg-white border border-brand-border rounded-md text-brand-black focus:outline-none focus:ring-2 focus:ring-brand-green appearance-none"
          >
            <option value="">All Entity Types</option>
            <option value="laptops">laptops</option>
            <option value="accessories">accessories</option>
            <option value="purchases">purchases</option>
            <option value="sales">sales</option>
            <option value="returns">returns</option>
            <option value="repair_jobs">repair_jobs</option>
            <option value="expenses">expenses</option>
            <option value="customers">customers</option>
            <option value="suppliers">suppliers</option>
          </select>
          <ChevronDown className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-brand-muted" />
        </div>

        <div className="relative">
          <select
            value={action}
            onChange={(e) => {
              setAction(e.target.value);
              setPage(0);
            }}
            className="h-9 pl-3 pr-8 bg-white border border-brand-border rounded-md text-brand-black focus:outline-none focus:ring-2 focus:ring-brand-green appearance-none"
          >
            <option value="">All Actions</option>
            <option value="INSERT">INSERT</option>
            <option value="UPDATE">UPDATE</option>
            <option value="DELETE">DELETE</option>
          </select>
          <ChevronDown className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-brand-muted" />
        </div>
      </div>

      {/* Error */}
      {isError && <ErrorAlert message={parseDatabaseError(error)} onRetry={() => refetch()} />}

      {/* Loading */}
      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 text-brand-green animate-spin" />
        </div>
      )}

      {/* Table */}
      {!isLoading && !isError && (
        <>
          {logs.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center bg-white border rounded-xl">
              <Activity className="w-12 h-12 text-brand-muted/30 mb-3" />
              <p className="font-bold text-brand-black">No audit logs found</p>
              <p className="text-brand-muted mt-1">Adjust your filters to search historical changes.</p>
            </div>
          ) : (
            <div className="bg-white border border-brand-border rounded-xl overflow-hidden shadow-xs">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-brand-bg border-b border-brand-border text-brand-muted font-semibold uppercase tracking-wide">
                    <tr>
                      <th className="text-left px-4 py-3">Timestamp</th>
                      <th className="text-left px-4 py-3">Action</th>
                      <th className="text-left px-4 py-3">Entity Type</th>
                      <th className="text-left px-4 py-3">Entity ID</th>
                      <th className="text-left px-4 py-3">Changed By</th>
                      <th className="text-right px-4 py-3">Data Diff</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-brand-border">
                    {logs.map((log: AuditLogRecord) => {
                      const isExpanded = expandedId === log.id;
                      return (
                        <React.Fragment key={log.id}>
                          <tr
                            onClick={() => setExpandedId(isExpanded ? null : log.id)}
                            className="hover:bg-brand-bg/60 cursor-pointer transition-colors"
                          >
                            <td className="px-4 py-3 text-brand-muted whitespace-nowrap">
                              <span className="flex items-center space-x-1">
                                <CalendarDays className="w-3.5 h-3.5 text-brand-green shrink-0" />
                                <span>{formatDate(log.changed_at)}</span>
                              </span>
                            </td>
                            <td className="px-4 py-3 font-bold">
                              <span
                                className={`px-2 py-0.5 rounded text-[10px] uppercase font-mono ${
                                  log.action === 'INSERT'
                                    ? 'bg-green-100 text-green-800'
                                    : log.action === 'UPDATE'
                                    ? 'bg-blue-100 text-blue-800'
                                    : 'bg-red-100 text-red-800'
                                }`}
                              >
                                {log.action}
                              </span>
                            </td>
                            <td className="px-4 py-3 font-bold text-brand-black font-mono">
                              {log.entity_type}
                            </td>
                            <td className="px-4 py-3 font-mono text-brand-muted text-[11px]">
                              {log.entity_id || '—'}
                            </td>
                            <td className="px-4 py-3 text-brand-muted">
                              <span className="flex items-center space-x-1 font-medium text-brand-black">
                                <User className="w-3.5 h-3.5 text-brand-muted" />
                                <span>{log.changed_by_profile?.full_name ?? 'System'}</span>
                              </span>
                            </td>
                            <td className="px-4 py-3 text-right">
                              <span className="text-brand-green font-semibold inline-flex items-center space-x-1">
                                <span>{isExpanded ? 'Hide Diff' : 'View Diff'}</span>
                                {isExpanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                              </span>
                            </td>
                          </tr>

                          {isExpanded && (
                            <tr className="bg-brand-bg/80">
                              <td colSpan={6} className="p-4 space-y-2 font-mono text-[11px]">
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                  <div>
                                    <p className="font-bold text-red-700 uppercase mb-1">Old Data Snapshot</p>
                                    <pre className="p-2.5 bg-white border rounded overflow-x-auto max-h-48 text-[10px] text-brand-black">
                                      {log.old_data ? JSON.stringify(log.old_data, null, 2) : 'null'}
                                    </pre>
                                  </div>

                                  <div>
                                    <p className="font-bold text-green-700 uppercase mb-1">New Data Snapshot</p>
                                    <pre className="p-2.5 bg-white border rounded overflow-x-auto max-h-48 text-[10px] text-brand-black">
                                      {log.new_data ? JSON.stringify(log.new_data, null, 2) : 'null'}
                                    </pre>
                                  </div>
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {totalCount > PAGE_SIZE && <Pagination page={page} totalCount={totalCount} onPageChange={setPage} />}
        </>
      )}
    </div>
  );
};
